import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type User from 'frontend-utils/models/user';
import { THEME, type Theme } from 'frontend-utils/utils/prism/theme';

export default class HotelSettingsThemeController extends Controller {
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare theme: Services['theme'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  saveThemeTask = task(
    { restartable: true },
    async (user: User): Promise<void> => {
      try {
        await user.save({
          adapterOptions: {
            usePatch: true,
          },
        });
        this.theme.setTheme();
        this.toast.success(this.intl.t('settings.theme.update_success'), {
          showIcon: true,
        });
        this.intercom.updateTheme(user.theme);
      } catch {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          title: this.intl.t('user.update_error'),
          text: this.intl.t('general.error_title'),
        });
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get highContrast() {
    return this.hotelAndMarketService.user?.theme.includes('highcontrast');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get themeValue() {
    if (!this.theme.hasPermissions) {
      return THEME.LIGHT;
    }

    if (!this.highContrast) {
      return this.hotelAndMarketService.user?.theme ?? THEME.LIGHT;
    }
    switch (this.hotelAndMarketService.user?.theme) {
      case THEME.HIGH_CONTRAST_DARK:
        return THEME.DARK;
      case THEME.HIGH_CONTRAST_LIGHT:
        return THEME.LIGHT;
      case THEME.HIGH_CONTRAST_SYSTEM:
        return THEME.SYSTEM;
      default:
        return THEME.LIGHT;
    }
  }

  @action handleThemeChange(event: Event): void {
    this.selectTheme((event?.target as HTMLInputElement)?.value as Theme);
  }

  @action selectTheme(
    theme: Theme | null,
    highContrast: boolean | null = null,
  ): void {
    const highContrastEnabled = highContrast ?? this.highContrast;
    let themeValue = theme ?? this.themeValue;
    if (highContrastEnabled) {
      switch (themeValue) {
        case THEME.LIGHT:
          themeValue = THEME.HIGH_CONTRAST_LIGHT;
          break;
        case THEME.DARK:
          themeValue = THEME.HIGH_CONTRAST_DARK;
          break;
        case THEME.SYSTEM:
          themeValue = THEME.HIGH_CONTRAST_SYSTEM;
          break;
        default:
          break;
      }
    }

    if (theme) {
      this.usageAnalytics.track('Theme Changed', {
        eventTrigger: `theme-settings-${theme}-card`,
        theme: themeValue,
      });
    }

    // insert the new theme in the user object
    const user = this.hotelAndMarketService.user;
    user.theme = themeValue;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.saveThemeTask.perform(user);
  }

  @action toggleHighContrast(highContrast: boolean): void {
    this.usageAnalytics.track('High Contrast Mode Toggled', {
      eventTrigger: 'theme-high-contrast-toggle',
      highContrast,
    });

    this.selectTheme(null, highContrast);
  }
}
